/**
 * Strategy Lab: BE chart series for indicator confluences
 * (`ohlc_stats.confluences.indicator_series.<slot key>`, handoff §1.6c).
 *
 * One entry per indicator confluence on the chart's timeframe, one value per candle from
 * `first_bar_time`. `pane: "price"` draws over the candles (MAs, bands, channels);
 * `pane: "sub"` gets its own panel (MACD, ATR, ADR, correlation, Mansfield). These are the
 * numbers the confluence tested — draw them as given, never recompute.
 */

export type StrategyRdSeriesLine = {
  name: string;
  role: string;
  /** Aligned 1:1 with the chart candles (null = warming up / not covered). */
  values: Array<number | null>;
};

export type StrategyRdChartSeries = {
  /** Slot key (+ `:sub` for % from MA's optional panel). */
  key: string;
  conceptId: string;
  /** `volume` = drawn as the chart's volume panel (bars + normal / threshold lines). */
  pane: "price" | "sub" | "volume";
  title: string;
  lines: StrategyRdSeriesLine[];
  histogram: { name: string; values: Array<number | null> } | null;
  levels: Array<{ name: string; value: number }>;
  /** Bars to shade (TTM / ATR squeeze). */
  squeezeOn: Array<boolean> | null;
  /** Volume panel: bars that pass the volume test (drawn brighter). */
  passes?: Array<boolean> | null;
  /** Volume panel: 1 up bar (close > prior close), −1 down, 0 flat — colours the bars. */
  barSign?: Array<number | null> | null;
  /** Lab chart group (recipe chip): `conf-N`, or `volume` for the shared volume panel. */
  group?: string | null;
};

/**
 * `values[k]` belongs to `candles[k]` of the same response (BE contract). Stamp each series
 * with those candle times so the chart places values by time — the chart may show a
 * different window (scan window merge, per-setup range) than the response list.
 * Series whose length doesn't match the response candles are left as they are.
 */
export function strategyRdStatsWithSeriesBarTimes<
  T extends { confluences?: Record<string, unknown> | null } | null | undefined,
>(ohlcStats: T, responseCandles: ReadonlyArray<{ timestamp?: string | null }> | null | undefined): T {
  const confluences = ohlcStats?.confluences;
  const series = asRecord(confluences?.indicator_series);
  const times = (responseCandles ?? []).map((c) => (c?.timestamp || "").trim());
  if (!ohlcStats || !series || times.length === 0 || times.some((t) => !t)) return ohlcStats;
  const next: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(series)) {
    const entry = asRecord(raw);
    if (!entry) {
      next[key] = raw;
      continue;
    }
    const lines = Array.isArray(entry.lines) ? entry.lines : [];
    const firstValues = asRecord(lines[0])?.values;
    const len =
      num(entry.bars) ??
      (Array.isArray(entry.values) ? entry.values.length : null) ??
      (Array.isArray(firstValues) ? firstValues.length : null);
    next[key] = len === times.length ? { ...entry, bar_times: times } : entry;
  }
  return {
    ...ohlcStats,
    confluences: { ...confluences, indicator_series: next },
  } as T;
}

/** Most sub-panels the Lab chart mounts (one Vela pane each). */
export const STRATEGY_RD_MAX_SUB_PANELS = 4;

const CONCEPT_TITLES: Record<string, string> = {
  pct_from_ma: "% from MA",
  ma_stack: "MA stack",
  atr_ext_from_ma: "ATR extension",
  ttm_squeeze: "TTM squeeze",
  coil_atr_squeeze: "ATR squeeze",
  atr_regime: "ATR regime",
  adr_pct: "ADR %",
  macd: "MACD",
  corr_rs: "Correlated RS",
  mansfield_rs: "Mansfield RS",
  rvol_strong: "Strong relative volume",
  vol_dryup: "Volume dry-up",
  vol_expansion: "Volume expansion",
  vol_vs_eod_pace: "Volume pace",
  up_down_vol_ratio: "Up/down volume",
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function timeMs(value: unknown): number | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const ms = Date.parse(value.trim().replace(/\+00:00$/, "Z"));
  return Number.isFinite(ms) ? ms : null;
}

function num(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Chart index of the series' first value. The series runs on the response candles, so
 * `first_bar_time` is normally on the chart; when it's earlier (warm-up), the series is
 * tail-aligned (it ends on the same last candle). Null = can't line it up — skip it.
 */
function alignOffset(
  firstBarMs: number | null,
  bars: number,
  chartTimesMs: readonly number[]
): number | null {
  const n = chartTimesMs.length;
  if (n === 0 || bars <= 0) return null;
  if (firstBarMs != null) {
    const idx = chartTimesMs.indexOf(firstBarMs);
    if (idx >= 0) return idx;
    if (firstBarMs < chartTimesMs[0]! && bars >= n) return -(bars - n);
    return null;
  }
  return bars === n ? 0 : null;
}

/** Where each series value goes on the chart: an index, or -1 when that candle isn't shown. */
type SeriesPlacement = (j: number) => number;

function alignValues(
  raw: unknown,
  place: SeriesPlacement,
  chartLength: number
): Array<number | null> {
  const values = Array.isArray(raw) ? raw : [];
  const out: Array<number | null> = new Array(chartLength).fill(null);
  for (let j = 0; j < values.length; j += 1) {
    const i = place(j);
    if (i < 0 || i >= chartLength) continue;
    out[i] = num(values[j]);
  }
  return out;
}

function parseLines(raw: unknown, place: SeriesPlacement, n: number): StrategyRdSeriesLine[] {
  if (!Array.isArray(raw)) return [];
  const out: StrategyRdSeriesLine[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (!row || !Array.isArray(row.values)) continue;
    out.push({
      name: String(row.name ?? "").trim() || String(row.role ?? "value"),
      role: String(row.role ?? "value").trim().toLowerCase(),
      values: alignValues(row.values, place, n),
    });
  }
  return out;
}

function alignFlags(raw: unknown, place: SeriesPlacement, n: number): boolean[] | null {
  if (!Array.isArray(raw)) return null;
  const out: boolean[] = new Array(n).fill(false);
  raw.forEach((v, j) => {
    const i = place(j);
    if (i >= 0 && i < n) out[i] = v === true;
  });
  return out;
}

function parseLevels(raw: unknown): Array<{ name: string; value: number }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ name: string; value: number }> = [];
  for (const item of raw) {
    const row = asRecord(item);
    const value = num(row?.value);
    if (!row || value == null) continue;
    out.push({ name: String(row.name ?? "").trim(), value });
  }
  return out;
}

/**
 * Parse + align every BE indicator series onto the chart candles. Price overlays first,
 * then sub-panels in slot order (% from MA's optional `sub` panel follows its MA).
 */
export function strategyRdChartSeriesFromResponse(
  indicatorSeries: Record<string, unknown> | null | undefined,
  chartTimesMs: readonly number[]
): StrategyRdChartSeries[] {
  if (!indicatorSeries || chartTimesMs.length === 0) return [];
  const n = chartTimesMs.length;
  const chartIndexByMs = new Map<number, number>();
  chartTimesMs.forEach((t, i) => chartIndexByMs.set(t, i));
  const price: StrategyRdChartSeries[] = [];
  const sub: StrategyRdChartSeries[] = [];
  for (const [slotKey, rawEntry] of Object.entries(indicatorSeries)) {
    const entry = asRecord(rawEntry);
    if (!entry) continue;
    const bars = num(entry.bars) ?? (Array.isArray(entry.values) ? entry.values.length : 0);
    // Preferred: the response candle times (values[k] ↔ candles[k]) → place by time.
    const barTimes = Array.isArray(entry.bar_times) ? (entry.bar_times as unknown[]) : null;
    let place: SeriesPlacement | null = null;
    if (barTimes && barTimes.length === bars) {
      const idx = barTimes.map((t) => {
        const ms = timeMs(t);
        return ms == null ? -1 : chartIndexByMs.get(ms) ?? -1;
      });
      if (idx.some((i) => i >= 0)) place = (j) => idx[j] ?? -1;
    }
    // Fallback: count from first_bar_time on the chart's own candles.
    const offset = place ? 0 : alignOffset(timeMs(entry.first_bar_time), bars, chartTimesMs);
    if (!place && offset != null) place = (j) => offset + j;
    if (!place) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[Strategy Lab] indicator series not aligned to chart candles", {
          slotKey,
          first_bar_time: entry.first_bar_time,
          bars,
          chartFirst: new Date(chartTimesMs[0]!).toISOString(),
          chartBars: chartTimesMs.length,
        });
      }
      continue;
    }
    const conceptId = String(entry.concept_id ?? entry.kind ?? slotKey).trim().toLowerCase();
    const group =
      typeof entry.chip_key === "string" && entry.chip_key.trim() ? entry.chip_key.trim() : null;
    const benchmark =
      typeof entry.benchmark_label === "string" && entry.benchmark_label.trim()
        ? entry.benchmark_label.trim()
        : null;
    const baseTitle = CONCEPT_TITLES[conceptId] ?? slotKey.replace(/_/g, " ");
    const nth = slotKey.includes("#") ? ` (${slotKey.split("#")[1]})` : "";
    const title = `${baseTitle}${nth}${benchmark ? ` · ${benchmark}` : ""}`;

    let lines = parseLines(entry.lines, place, n);
    // Older Mansfield shape: bare `values` + `zero_line`.
    if (lines.length === 0 && Array.isArray(entry.values)) {
      lines = [{ name: baseTitle, role: "value", values: alignValues(entry.values, place, n) }];
    }
    const hist = asRecord(entry.histogram);
    const flags = asRecord(entry.flags);
    const squeezeOn = alignFlags(flags?.squeeze_on, place, n);
    const passes = alignFlags(flags?.passes, place, n);
    const barSign = Array.isArray(entry.bar_sign) ? alignValues(entry.bar_sign, place, n) : null;
    const levels = parseLevels(entry.levels);
    const histogram =
      hist && Array.isArray(hist.values)
        ? { name: String(hist.name ?? "Histogram"), values: alignValues(hist.values, place, n) }
        : null;
    let extraLines = parseLines(asRecord(entry.sub)?.lines, place, n);
    // Pace: projected day volume is on the volume scale — keep it with the cumulative curve,
    // leave the ×-pace line alone in the small panel.
    if (conceptId === "vol_vs_eod_pace") {
      lines = [...lines, ...extraLines.filter((l) => l.role === "projected")];
      extraLines = extraLines.filter((l) => l.role !== "projected");
    }
    // Up/down: the volume bars (coloured by bar_sign) go on the volume panel; the ratio
    // keeps its own panel (≈1 next to share counts would flatten it).
    if (conceptId === "up_down_vol_ratio" && histogram) {
      sub.push({
        key: `${slotKey}:volume`,
        conceptId,
        pane: "volume",
        title,
        lines: [],
        histogram,
        levels: [],
        squeezeOn: null,
        passes: null,
        barSign,
        group: "volume",
      });
    }
    if (levels.length === 0 && num(entry.zero_line) != null) {
      levels.push({ name: "zero", value: num(entry.zero_line)! });
    }
    const series: StrategyRdChartSeries = {
      key: slotKey,
      conceptId,
      pane: entry.pane === "price" ? "price" : entry.pane === "volume" ? "volume" : "sub",
      title,
      lines,
      histogram: conceptId === "up_down_vol_ratio" ? null : histogram,
      levels,
      squeezeOn,
      passes,
      barSign: conceptId === "up_down_vol_ratio" ? null : barSign,
      group: entry.pane === "volume" ? "volume" : group,
    };
    if (series.lines.length === 0 && !series.histogram) continue;
    (series.pane === "price" ? price : sub).push(series);

    const extra = asRecord(entry.sub);
    if (extra && extraLines.length > 0) {
      sub.push({
        key: `${slotKey}:sub`,
        conceptId,
        pane: "sub",
        title: `${baseTitle}${nth} · ${extraLines[0]!.name}`,
        lines: extraLines,
        histogram: null,
        levels: parseLevels(extra.levels),
        squeezeOn: null,
        group,
      });
    }
  }
  // One volume panel: bars drawn once; every volume confluence adds its normal / threshold
  // lines; a bar that passes any of them is highlighted; up/down signs colour the bars.
  const volume = sub.filter((e) => e.pane === "volume");
  const others = sub.filter((e) => e.pane !== "volume");
  const merged: StrategyRdChartSeries[] = [];
  if (volume.length > 0) {
    const passes = volume.some((e) => e.passes)
      ? Array.from({ length: n }, (_, i) => volume.some((e) => e.passes?.[i] === true))
      : null;
    merged.push({
      key: "volume",
      conceptId: "volume",
      pane: "volume",
      title: "Volume",
      lines: volume.flatMap((e) =>
        e.lines.map((l) => ({ ...l, name: volume.length > 1 ? `${e.title}: ${l.name}` : l.name }))
      ),
      histogram: volume.find((e) => e.histogram)?.histogram ?? null,
      levels: [],
      squeezeOn: null,
      passes,
      barSign: volume.find((e) => e.barSign)?.barSign ?? null,
      group: "volume",
    });
  }
  // Panel budget: every confluence's main panel first; the optional `sub` extras (e.g. the
  // RVOL multiple, which repeats the volume panel's threshold) only when there's room.
  const main = others.filter((e) => !e.key.endsWith(":sub"));
  const extras = others.filter((e) => e.key.endsWith(":sub"));
  return [...price, ...[...merged, ...main, ...extras].slice(0, STRATEGY_RD_MAX_SUB_PANELS)];
}

/**
 * FE-computed lanes that duplicate a BE series (draw the tested numbers once):
 * MACD panel, TTM's Bollinger Bands, ATR panel.
 */
export function strategyRdLanesCoveredBySeries(
  indicatorSeries: Record<string, unknown> | null | undefined
): Set<string> {
  const out = new Set<string>();
  for (const [slotKey, raw] of Object.entries(indicatorSeries ?? {})) {
    const concept = String(asRecord(raw)?.concept_id ?? asRecord(raw)?.kind ?? slotKey)
      .trim()
      .toLowerCase();
    if (concept.startsWith("macd")) out.add("macd");
    if (concept === "ttm_squeeze") out.add("bb");
    if (concept.startsWith("atr_regime") || concept === "coil_atr_squeeze") out.add("atr");
  }
  return out;
}

/**
 * Concept markers aimed at an FE pane that a BE series replaced (MACD / ATR) move onto
 * that BE sub-panel (`tessera-rd-sub-N`). Others keep their pane.
 */
export function strategyRdRetargetPaneId(
  drawing: { id: string; paneId?: string | null },
  series: readonly StrategyRdChartSeries[]
): string | null {
  const paneId = (drawing.paneId || "").trim();
  if (!paneId) return null;
  const sub = series.filter((e) => e.pane !== "price");
  const id = drawing.id.toLowerCase();
  let index = -1;
  if (paneId === "volume") {
    // Volume badges (RVol 1.6×) follow the BE volume panel when it replaces the chart's.
    index = sub.findIndex((e) => e.pane === "volume");
  } else if (paneId === "tessera-macd") {
    index = sub.findIndex((e) => e.conceptId.startsWith("macd"));
  } else if (paneId === "tessera-panes" && id.includes("atr_regime")) {
    index = sub.findIndex((e) => e.conceptId.startsWith("atr_regime"));
  } else if (paneId === "tessera-panes" && id.includes("coil_atr_squeeze")) {
    index = sub.findIndex((e) => e.conceptId === "coil_atr_squeeze");
  } else if (paneId === "tessera-panes" && id.includes("mansfield")) {
    // Mansfield's value lives only in its BE panel; with no panel, badge it on price.
    index = sub.findIndex((e) => e.conceptId.includes("mansfield"));
    if (index < 0) return null;
  }
  return index >= 0 ? `tessera-rd-sub-${index}` : paneId;
}

const ROLE_COLORS: Record<string, string> = {
  basis: "#9CA3AF",
  bb_upper: "#8B5CF6",
  bb_lower: "#8B5CF6",
  kc_upper: "#F59E0B",
  kc_lower: "#F59E0B",
  band_max: "#E00101",
  band_min: "#F59E0B",
  macd: "#38BBD1",
  signal: "#F59E0B",
  value: "#38BBD1",
  average: "#F59E0B",
  corr: "#8B5CF6",
  rs_pct: "#06E363",
  // Volume confluences (§1.6d)
  baseline: "#9CA3AF",
  threshold: "#F59E0B",
  cumulative: "#38BBD1",
  usual: "#9CA3AF",
  projected: "#8B5CF6",
  rvol: "#38BBD1",
  ratio: "#38BBD1",
  pace: "#38BBD1",
};
const MA_COLORS = ["#38BBD1", "#F59E0B", "#8B5CF6"];

/** Readable name per line role (BE names are terse: "BB upper", "Basis SMA 20"). */
const ROLE_NAMES: Record<string, string> = {
  bb_upper: "Bollinger upper band",
  bb_lower: "Bollinger lower band",
  kc_upper: "Keltner upper band",
  kc_lower: "Keltner lower band",
  macd: "MACD line",
  signal: "Signal line",
};

/** "+3 ATR" / "-2 ATR" → { sign, mult } for ATR-extension band lines. */
function parseAtrBand(name: string): { sign: "+" | "\u2212"; mult: string } | null {
  const m = /^([+-])\s*([\d.]+)\s*ATR$/i.exec(name.trim());
  if (!m) return null;
  return { sign: m[1] === "-" ? "\u2212" : "+", mult: m[2]! };
}

/**
 * Short on-chart end label for price-pane lines that need one (MAs, min / max distance
 * bands); null = legend only. "Max +3×ATR", "Min −2×ATR", "SMA 50".
 */
export function strategyRdSeriesEndLabel(
  line: Pick<StrategyRdSeriesLine, "name" | "role">
): string | null {
  if (line.role === "ma") return line.name.trim() || null;
  if (line.role === "band_max" || line.role === "band_min") {
    const band = parseAtrBand(line.name);
    const kind = line.role === "band_max" ? "Max" : "Min";
    return band ? `${kind} ${band.sign}${band.mult}\u00d7ATR` : `${kind} ${line.name.trim()}`;
  }
  return null;
}

/**
 * Legend title for one line: "<confluence> · <what it is>", e.g.
 * "TTM squeeze · Bollinger upper band", "TTM squeeze · Middle (SMA 20)".
 */
export function strategyRdSeriesLineLabel(
  entry: Pick<StrategyRdChartSeries, "title">,
  line: Pick<StrategyRdSeriesLine, "name" | "role">
): string {
  const raw = line.name.trim();
  if (line.role === "band_max" || line.role === "band_min") {
    const band = parseAtrBand(raw);
    const kind = line.role === "band_max" ? "Max distance" : "Min distance";
    const side = band ? (band.sign === "+" ? "upper" : "lower") : "";
    return band
      ? `${entry.title} · ${kind} ${band.sign}${band.mult}\u00d7ATR (${side})`
      : `${entry.title} · ${kind} ${raw}`;
  }
  const name =
    ROLE_NAMES[line.role] ??
    (line.role === "basis" ? raw.replace(/^basis\s*/i, "Middle (").replace(/$/, ")") : raw);
  return `${entry.title} · ${name === "Middle ()" ? "Middle" : name}`;
}

/** Legend text for shaded bars (what the shading means), by concept. */
export function strategyRdSqueezeLegend(conceptId: string): string {
  if (conceptId === "ttm_squeeze") {
    return "Shaded bars = squeeze on (Bollinger Bands inside Keltner Channel)";
  }
  if (conceptId === "coil_atr_squeeze") {
    return "Shaded bars = squeeze on (ATR below its average)";
  }
  return "Shaded bars = squeeze on";
}

/** Line colour + dash by role (MAs cycle through the palette in order). */
export function strategyRdSeriesLineStyle(
  line: Pick<StrategyRdSeriesLine, "role">,
  maIndex: number
): { color: string; lineStyle: "solid" | "dashed"; width: number } {
  if (line.role === "ma") {
    return { color: MA_COLORS[maIndex % MA_COLORS.length]!, lineStyle: "solid", width: 2 };
  }
  // Bollinger = solid purple, Keltner = dashed orange, so the two envelopes read apart.
  if (line.role.startsWith("bb_")) {
    return { color: ROLE_COLORS[line.role]!, lineStyle: "solid", width: 1 };
  }
  const dashed =
    line.role === "threshold" ||
    line.role === "usual" ||
    line.role === "projected" ||
    line.role.startsWith("kc_") ||
    line.role.startsWith("band_") ||
    line.role === "average" ||
    line.role === "signal";
  return {
    color: ROLE_COLORS[line.role] ?? "#38BBD1",
    lineStyle: dashed ? "dashed" : "solid",
    width: line.role === "basis" ? 1 : 2,
  };
}
